import React, { useEffect, useRef, useState } from "react";
import "./BinnieWidget.css";
import "./BinnieUI.css";
import { render as renderUi, pageCard } from "./BinnieUI";

/*
 * Binnie 위젯 — 포트폴리오 우하단 플로팅 챗봇. Binnie 공개 API(SSE)와 통신한다.
 * SSE 이벤트: status(진행 상태) / tool(검색 알림) / delta(본문) / suggestions(다음 질문 추천) / done / error
 *
 * 아이콘 출처: Lucide Icons (https://lucide.dev) — ISC License, Copyright (c) Lucide Contributors.
 *   - bot:      https://unpkg.com/lucide-static@latest/icons/bot.svg
 *   - x:        https://unpkg.com/lucide-static@latest/icons/x.svg
 *   - arrow-up: https://unpkg.com/lucide-static@latest/icons/arrow-up.svg
 *   외부 이미지 로드 없이 path 를 인라인 SVG 로 포함한다.
 */

const INITIAL = [
  "주요 프로젝트와 담당 역할을 알려주세요",
  "백엔드에서 어떤 업무를 수행할 수 있나요?",
  "보유 자격증을 정리해 주세요",
];

const SVG_PROPS = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, focusable: "false" };

// Lucide "bot"
// wink=true 면 오른쪽 눈(M15 13v2)에 주기적 윙크 애니메이션 클래스가 붙는다 (런처 버튼 전용)
function BotIcon({ wink = false }) {
  return (
    <svg {...SVG_PROPS} className={wink ? "binnie-bot binnie-bot-wink" : "binnie-bot"}>
      <path d="M12 8V4H8" />
      <rect width="16" height="12" x="4" y="8" rx="2" />
      <path d="M2 14h2" />
      <path d="M20 14h2" />
      <path className="binnie-eye binnie-eye-r" d="M15 13v2" />
      <path className="binnie-eye binnie-eye-l" d="M9 13v2" />
    </svg>
  );
}
// Lucide "x"
function XIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
// Lucide "arrow-up"
function ArrowUpIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="m5 12 7-7 7 7" />
      <path d="M12 19V5" />
    </svg>
  );
}

function toolLabel(ev) {
  if (ev.name === "search_notes") return `검색: ${(ev.args && ev.args.query) || "노션 기록"}`;
  if (ev.name === "read_page") return "노트 읽기";
  return String(ev.name || "도구 실행");
}

export default function BinnieWidget({
  endpoint,
  name = "Binnie",
  subtitle = "포트폴리오 안내 챗봇", // 하위 호환용으로 받기만 하고 렌더하지 않는다.
  greeting = "안녕하세요! 방문해주셔서 감사합니다. 윤성빈의 정보, 학습 내용(노션 기록 기반), 프로젝트, 업무 수행 가능 영역 등 다양한 정보를 물어보세요!",
}) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: greeting }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [tools, setTools] = useState([]);
  const [suggestions, setSuggestions] = useState(INITIAL);
  const [unread, setUnread] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const openRef = useRef(open); // 스트리밍 완료 시점의 열림 상태를 읽기 위해(클로저의 stale 값 방지)

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, status, tools, suggestions, open]);
  useEffect(() => {
    openRef.current = open;
    if (open) { setUnread(false); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open]);

  async function send(text) {
    const q = (text || "").trim();
    if (!q || busy) return;
    setBusy(true); setInput(""); setSuggestions([]); setTools([]); setStatus("연결 중…");
    const history = [...messages.slice(1), { role: "user", content: q }];
    setMessages((m) => [...m, { role: "user", content: q }]);
    let acc = "";
    let started = false;
    const segs = []; // text | ui | page 세그먼트. 생성형 UI 블록이 산문 사이에 들어온다.
    try {
      const res = await fetch(endpoint, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ messages: history }) });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || `HTTP ${res.status}`); }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const part of parts) {
          const line = part.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const ev = JSON.parse(line.slice(5));
          if (ev.type === "status") setStatus(ev.text);
          else if (ev.type === "tool") { const label = toolLabel(ev); setTools((t) => [...t, label]); }
          else if (ev.type === "delta" || ev.type === "ui" || ev.type === "page") {
            if (ev.type === "delta") {
              acc += ev.text;
              if (!segs.length || segs[segs.length - 1].kind !== "text") segs.push({ kind: "text", text: "" });
              segs[segs.length - 1].text += ev.text;
            } else if (ev.type === "ui") {
              segs.push({ kind: "ui", spec: ev.spec });
              acc += "\n```ui\n" + JSON.stringify(ev.spec) + "\n```\n";
            } else {
              segs.push({ kind: "page", url: ev.url, title: ev.title, summary: ev.summary });
              acc += `\n[페이지: ${ev.title}](${ev.url})\n`;
            }
            // 루프 안 클로저가 바뀌는 변수를 참조하지 않도록 스냅샷 고정
            const snapshot = { role: "assistant", content: acc, segments: segs.map((s) => (s.kind === "text" ? { ...s } : s)) };
            if (!started) { started = true; setStatus(""); setMessages((m) => [...m, snapshot]); }
            else setMessages((m) => [...m.slice(0, -1), snapshot]);
          } else if (ev.type === "suggestions") setSuggestions(ev.items || []);
          else if (ev.type === "error") throw new Error(ev.message);
        }
      }
      if (!started) setMessages((m) => [...m, { role: "assistant", content: "(빈 응답)" }]);
      if (!openRef.current) setUnread(true);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `죄송해요, 오류가 났어요. (${e.message})`, error: true }]);
    } finally {
      setBusy(false); setStatus("");
    }
  }

  return (
    <>
      <button type="button" className={`binnie-fab ${open ? "is-open" : ""}`} onClick={() => setOpen((o) => !o)} aria-label={`${name} 챗봇 ${open ? "닫기" : "열기"}`} aria-expanded={open}>
        {open ? <XIcon /> : <BotIcon wink />}
        {unread && !open && <span className="binnie-fab-dot" />}
      </button>

      {open && (
        <div className="binnie-panel" role="dialog" aria-label={`${name} 챗봇`}>
          <div className="binnie-head">
            <div className="binnie-avatar"><BotIcon /></div>
            <div className="binnie-title">{name}</div>
            <button type="button" className="binnie-close" onClick={() => setOpen(false)} aria-label="닫기"><XIcon /></button>
          </div>

          <div className="binnie-list" ref={listRef}>
            {messages.map((m, i) => (
              <div key={i} className={`binnie-row ${m.role}`}>
                {m.role === "assistant" && <div className="binnie-avatar sm"><BotIcon /></div>}
                <div className={`binnie-msg ${m.role} ${m.error ? "error" : ""}`}>{m.role === "assistant" ? <Segments message={m} /> : m.content}</div>
              </div>
            ))}
            {(status || tools.length > 0) && busy && (
              <div className="binnie-meta">
                {status && <div className="binnie-status"><span className="binnie-dots"><i /><i /><i /></span>{status}</div>}
                {tools.length > 0 && <div className="binnie-tools">{tools.map((t, i) => <span key={i}>{t}</span>)}</div>}
              </div>
            )}
            {!busy && suggestions.length > 0 && (
              <div className="binnie-suggest">
                <div className="binnie-suggest-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 14 4 9l5-5" /><path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11" /></svg>
                  <span>{messages.length <= 1 ? "이렇게 물어볼 수 있어요" : "이어서 물어보기"}</span>
                </div>
                <div className="binnie-suggest-list">
                  {suggestions.map((s) => (
                    <button key={s} type="button" onClick={() => send(s)}><span className="binnie-q">?</span>{s}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <form className="binnie-form" onSubmit={(e) => { e.preventDefault(); send(input); }}>
            <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} placeholder="질문을 입력하세요" maxLength={1000} disabled={busy} aria-label="질문 입력" />
            <button type="submit" disabled={busy || !input.trim()} aria-label="보내기"><ArrowUpIcon /></button>
          </form>
        </div>
      )}
    </>
  );
}

// 어시스턴트 메시지: 산문(마크다운) · 생성형 UI 블록 · 페이지 링크 카드 순서대로.
// 렌더러(BinnieUI)는 모든 문자열을 이스케이프한 HTML 을 만들므로 innerHTML 삽입이 안전하다.
function Segments({ message }) {
  const segs = message.segments && message.segments.length ? message.segments : [{ kind: "text", text: message.content }];
  return (
    <>
      {segs.map((s, i) =>
        s.kind === "text" ? <Markdown key={i} text={s.text} />
        : s.kind === "ui" ? <div key={i} className="binnie-ui" dangerouslySetInnerHTML={{ __html: renderUi([s.spec]) }} />
        : <div key={i} className="binnie-ui" dangerouslySetInnerHTML={{ __html: pageCard(s) }} />
      )}
    </>
  );
}

// 아주 작은 마크다운 → React (굵게, 코드, 링크, 목록, 표, 헤딩, 굵은 소제목). 외부 라이브러리 없이 유지.
const BLOCK_START = /^(#{1,6}\s|\s*[-*•]\s|\s*\d+\.\s|\||```)/;
const BOLD_LINE = /^\s*\*\*[^*]+\*\*\s*:?\s*$/; // 한 줄이 통째로 굵은 글씨 → 소제목으로 취급

function Markdown({ text }) {
  const lines = (text || "").split("\n");
  const out = [];
  let i = 0;
  const inline = (s) =>
    s.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(https?:[^)\s]+\))/g).map((p, k) => {
      if (/^\*\*.*\*\*$/.test(p)) return <strong key={k}>{p.slice(2, -2)}</strong>;
      if (/^`.*`$/.test(p)) return <code key={k}>{p.slice(1, -1)}</code>;
      const link = p.match(/^\[([^\]]+)\]\((https?:[^)\s]+)\)$/);
      if (link) return <a key={k} href={link[2]} target="_blank" rel="noopener noreferrer">{link[1]}</a>;
      return p;
    });
  while (i < lines.length) {
    const line = lines[i];
    // 표 행은 앞 파이프만 필수 — 모델이 마지막 파이프를 빠뜨려도 표로 인식
    if (/^\s*\|.+$/.test(line) && /^\s*\|[\s:|-]*-[\s:|-]*\|?\s*$/.test(lines[i + 1] || "")) {
      const cells = (l) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
      const head = cells(line); i += 2; const rows = [];
      while (i < lines.length && /^\s*\|.+$/.test(lines[i])) rows.push(cells(lines[i++]));
      out.push(
        <div className="binnie-table" key={out.length}>
          <table>
            <thead><tr>{head.map((c, k) => <th key={k}>{inline(c)}</th>)}</tr></thead>
            <tbody>{rows.map((r, k) => <tr key={k}>{r.map((c, j) => <td key={j}>{inline(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }
    if (/^\s*[-*•]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) items.push(lines[i++].replace(/^\s*[-*•]\s+/, ""));
      out.push(<ul key={out.length}>{items.map((it, k) => <li key={k}>{inline(it)}</li>)}</ul>);
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) items.push(lines[i++].replace(/^\s*\d+\.\s+/, ""));
      out.push(<ol key={out.length}>{items.map((it, k) => <li key={k}>{inline(it)}</li>)}</ol>);
      continue;
    }
    const h = line.match(/^(#{1,6})\s+(.*)/);
    if (h) {
      // #, ##, ### → h3 / ####이하 → h4 (말풍선 안에서는 두 단계만 쓴다)
      const Tag = h[1].length <= 3 ? "h3" : "h4";
      out.push(<Tag key={out.length}>{inline(h[2])}</Tag>); i++; continue;
    }
    if (line.startsWith("```")) {
      const buf = []; i++;
      while (i < lines.length && !lines[i].startsWith("```")) buf.push(lines[i++]);
      i++; out.push(<pre key={out.length}><code>{buf.join("\n")}</code></pre>); continue;
    }
    if (!line.trim()) { i++; continue; }
    if (BOLD_LINE.test(line)) { out.push(<p className="binnie-sub" key={out.length}>{inline(line.trim())}</p>); i++; continue; }
    const para = [line]; i++;
    while (i < lines.length && lines[i].trim() && !BLOCK_START.test(lines[i]) && !BOLD_LINE.test(lines[i])) para.push(lines[i++]);
    out.push(<p key={out.length}>{para.map((l, k) => <React.Fragment key={k}>{k > 0 && <br />}{inline(l)}</React.Fragment>)}</p>);
  }
  return <>{out}</>;
}
